import type { RouteRecordRaw } from 'vue-router'
import { useLocaleStore } from '@/stores/locale'
import { PROPERTY_TYPE_SLUG_PATTERN } from '@/utils/property-type-routes'

/**
 * Константы имён роутов
 */
export const ROUTES = {
  HOME: 'home',
  ABOUT: 'about',
  NEWS: 'news',
  HANDBOOK: 'handbook',
  SERVICES: 'services',
  NEWS_DETAIL: 'news-detail',
  HANDBOOK_DETAIL: 'handbook-detail',
  SERVICES_DETAIL: 'services-detail',
  // DEV-168: section-разделы /articles/{section} и /articles/{section}/{slug}
  ARTICLE_SECTION: 'article-section',
  ARTICLE_SECTION_DETAIL: 'article-section-detail',
  // DEV-168: SEO-посадочная в корне /{locale}/{slug}
  SEO_LANDING: 'seo-landing',
  PROPERTIES_CATALOG: 'properties-catalog',
  // DEV-2334: общий каталог постранично /properties/page/:page — эти ссылки отдаёт SSR
  PROPERTIES_CATALOG_PAGE: 'properties-catalog-page',
  // DEV-222: каталог по типу недвижимости /properties/:type[/page/:page]
  PROPERTIES_CATALOG_BY_TYPE: 'properties-catalog-by-type',
  PROPERTIES_CATALOG_BY_TYPE_PAGE: 'properties-catalog-by-type-page',
  PROPERTIES_CATALOG_BY_CITY: 'properties-catalog-by-city',
  // DEV-190: пагинация каталога города path-сегментом /properties/:city/page/:page
  PROPERTIES_CATALOG_BY_CITY_PAGE: 'properties-catalog-by-city-page',
  PROPERTY_DETAIL: 'property-detail',
  LOCATION_CITY: 'location-city',
  // DEV-1756/1758: хаб городов /locations
  LOCATIONS_HUB: 'locations-hub',
  // DEV-1762: хаб застройщиков /developers
  DEVELOPERS: 'developers',
  // DEV-1722: страница застройщика /developers/:slug
  DEVELOPER: 'developer',
  PARTNERS: 'partners',
  CAREERS: 'careers',
  PRIVACY: 'privacy-policy',
  COOKIE_POLICY: 'cookie-policy',
  TERMS: 'terms-of-use',
  LEGAL: 'legal-disclaimer',
} as const

/**
 * Разделы внутри /articles/{section}/{slug} (DEV-168).
 * Список должен совпадать с url_parser в backend/ssr и с маппингом
 * ArticleType → URL в frontend/vue-web-app/src/utils/article-routes.ts.
 */
export const ARTICLE_SECTIONS = ['analytics', 'buy-real-estate-vietnam', 'investment'] as const
export type ArticleSection = (typeof ARTICLE_SECTIONS)[number]

/**
 * Роуты приложения
 * ⚠️ ВАЖНО: Навигация slug-based (/:locale/news/:slug, не /:locale/news/:id)
 */
export const routes: RouteRecordRaw[] = [
  // DEV-1015: обезличенный лендинг презентации агента. TOP-LEVEL и ПЕРВЫМ — вне /:locale
  // и вне AppScaffold: без хедера/футера (без брендинга), локаль берётся из payload, а не
  // из URL, поэтому localeGuard его не переписывает (нет параметра :locale).
  {
    path: '/p/:token',
    name: 'presentation-landing',
    component: () => import('@/features/presentation/views/PresentationLandingView.vue'),
  },
  {
    path: '/:locale',
    component: () => import('@/components/layout/AppScaffold.vue'),
    children: [
      {
        path: '',
        name: ROUTES.HOME,
        component: () => import('@/features/home/views/HomeView.vue'),
      },
      {
        path: 'about',
        name: ROUTES.ABOUT,
        component: () => import('@/features/static/views/AboutView.vue'),
      },
      {
        path: 'news',
        name: ROUTES.NEWS,
        component: () => import('@/features/articles/views/ArticlesView.vue'),
        meta: { articleType: 1 }, // ArticleType.NEWS
      },
      {
        path: 'handbook',
        name: ROUTES.HANDBOOK,
        component: () => import('@/features/articles/views/ArticlesView.vue'),
        meta: { articleType: 2 }, // ArticleType.HANDBOOK
      },
      {
        path: 'services',
        name: ROUTES.SERVICES,
        component: () => import('@/features/articles/views/ArticlesView.vue'),
        meta: { articleType: 3 }, // ArticleType.SERVICES
      },
      {
        path: 'news/:slug', // ← SLUG, не ID!
        name: ROUTES.NEWS_DETAIL,
        component: () => import('@/features/articles/views/ArticleDetailsView.vue'),
        props: true,
        meta: { articleType: 1 }, // ArticleType.NEWS
      },
      {
        path: 'handbook/:slug',
        name: ROUTES.HANDBOOK_DETAIL,
        component: () => import('@/features/articles/views/ArticleDetailsView.vue'),
        props: true,
        meta: { articleType: 2 }, // ArticleType.HANDBOOK
      },
      {
        path: 'services/:slug',
        name: ROUTES.SERVICES_DETAIL,
        component: () => import('@/features/articles/views/ArticleDetailsView.vue'),
        props: true,
        meta: { articleType: 3 }, // ArticleType.SERVICES
      },
      // DEV-168: каталог раздела статей. Section валидируется через regex
      // в Vue Router — мимо проходит на 404. Список совпадает с ARTICLE_SECTIONS.
      {
        path: 'articles/:section(analytics|buy-real-estate-vietnam|investment)',
        name: ROUTES.ARTICLE_SECTION,
        component: () => import('@/features/articles/views/ArticlesView.vue'),
        props: true,
      },
      {
        path: 'articles/:section(analytics|buy-real-estate-vietnam|investment)/:slug',
        name: ROUTES.ARTICLE_SECTION_DETAIL,
        component: () => import('@/features/articles/views/ArticleDetailsView.vue'),
        props: true,
      },
      {
        path: 'properties',
        name: ROUTES.PROPERTIES_CATALOG,
        component: () =>
          import('@/features/residential-complex/views/ResidentialComplexesView.vue'),
      },
      {
        // DEV-2334: страница N общего каталога. SSR ссылается на /properties/page/N,
        // а без этого роута адрес уходил в properties/:city/:slug — «ЖК не найден».
        // Статичный сегмент page ранжируется выше параметров :type/:city.
        path: 'properties/page/:page(\\d+)',
        name: ROUTES.PROPERTIES_CATALOG_PAGE,
        component: () =>
          import('@/features/residential-complex/views/ResidentialComplexesView.vue'),
      },
      {
        // DEV-222: каталог по типу. Закрытая альтернация type-slug'ов ВЫШЕ
        // properties/:city — снимает коллизию city/type (как articles/:section(...)).
        path: `properties/:type(${PROPERTY_TYPE_SLUG_PATTERN})`,
        name: ROUTES.PROPERTIES_CATALOG_BY_TYPE,
        component: () =>
          import('@/features/residential-complex/views/ResidentialComplexesView.vue'),
        props: true,
      },
      {
        path: `properties/:type(${PROPERTY_TYPE_SLUG_PATTERN})/page/:page(\\d+)`,
        name: ROUTES.PROPERTIES_CATALOG_BY_TYPE_PAGE,
        component: () =>
          import('@/features/residential-complex/views/ResidentialComplexesView.vue'),
        props: true,
      },
      {
        path: 'properties/:city',
        name: ROUTES.PROPERTIES_CATALOG_BY_CITY,
        component: () =>
          import('@/features/residential-complex/views/ResidentialComplexesView.vue'),
        props: true,
      },
      {
        // DEV-190: пагинация path-сегментом. До properties/:city/:slug конфликта нет
        // (4 сегмента против 3), но держим рядом с базовым каталогом города.
        path: 'properties/:city/page/:page(\\d+)',
        name: ROUTES.PROPERTIES_CATALOG_BY_CITY_PAGE,
        component: () =>
          import('@/features/residential-complex/views/ResidentialComplexesView.vue'),
        props: true,
      },
      {
        path: 'properties/:city/:slug',
        name: ROUTES.PROPERTY_DETAIL,
        component: () =>
          import('@/features/residential-complex/views/ResidentialComplexDetailsView.vue'),
        props: true,
      },
      // DEV-1758: хаб городов. Строго до locations/:city — иначе голый
      // /locations уехал бы в гид города с пустым слагом, как это было с
      // /developers (DEV-1762).
      {
        path: 'locations',
        name: ROUTES.LOCATIONS_HUB,
        component: () => import('@/features/locations/views/LocationsHubView.vue'),
      },
      {
        path: 'locations/:city',
        name: ROUTES.LOCATION_CITY,
        component: () => import('@/features/locations/views/LocationCityView.vue'),
        props: true,
      },
      // DEV-1762: хаб застройщиков. Строго до developers/:slug — иначе голый
      // /developers уехал бы в страницу застройщика со слагом «».
      {
        path: 'developers',
        name: ROUTES.DEVELOPERS,
        component: () => import('@/features/developer/views/DevelopersHubView.vue'),
      },
      // DEV-1722: страница застройщика. Слаг выводится из названия — правило
      // общее с SSR (src/utils/developer-slug.ts), иначе бот и человек по
      // одному адресу получат разные страницы.
      {
        path: 'developers/:slug',
        name: ROUTES.DEVELOPER,
        component: () => import('@/features/developer/views/DeveloperView.vue'),
        props: true,
      },
      {
        path: 'partners',
        name: ROUTES.PARTNERS,
        component: () => import('@/features/static/views/PartnersView.vue'),
      },
      {
        path: 'careers',
        name: ROUTES.CAREERS,
        component: () => import('@/features/static/views/CareersView.vue'),
      },
      {
        path: 'privacy-policy',
        name: ROUTES.PRIVACY,
        component: () => import('@/features/static/views/PrivacyPolicyView.vue'),
      },
      {
        path: 'cookie-policy',
        name: ROUTES.COOKIE_POLICY,
        component: () => import('@/features/static/views/CookiePolicyView.vue'),
      },
      {
        path: 'terms-of-use',
        name: ROUTES.TERMS,
        component: () => import('@/features/static/views/TermsOfUseView.vue'),
      },
      {
        path: 'legal-disclaimer',
        name: ROUTES.LEGAL,
        component: () => import('@/features/static/views/LegalDisclaimerView.vue'),
      },
      // DEV-168: SEO-посадочная в корне /{locale}/{slug}.
      // ВАЖНО: этот роут должен быть последним из дочерних. Любая новая
      // статичная top-level страница (например, /:locale/blog) добавляется
      // ВЫШЕ этого роута, иначе она будет перехвачена SEO-landing.
      // На детальной view (`ArticleDetailsView`) проверяется, что
      // article.type === SEO_LANDING — иначе 404.
      {
        path: ':slug',
        name: ROUTES.SEO_LANDING,
        component: () => import('@/features/articles/views/ArticleDetailsView.vue'),
        props: true,
      },
    ],
  },
  // /sitemap.xml и /robots.txt до SPA не доходят — ingress отдаёт их в SSR
  // для всех клиентов (см. cloud/k8s-infrastructure web-app-ingress).
  // Решение владельца 09.10.2026 (DEV-2334): корень оставлен как есть — людям
  // клиентский редирект на сохранённую/браузерную локаль (по умолчанию /ru),
  // боты получают из SSR 301 на /en (x-default).
  {
    path: '/',
    redirect: () => {
      // Определяем локаль при первом заходе
      const localeStore = useLocaleStore()
      
      // Проверяем сохранённую локаль
      const saved = localStorage.getItem('locale')
      if (saved) {
        return `/${saved}`
      }
      
      // Определяем локаль браузера
      const browserLocale = localeStore.detectBrowserLocale()
      if (browserLocale) {
        return `/${browserLocale}`
      }
      
      // Дефолтная локаль
      return '/ru'
    },
  },
  {
    // 404 - catch all
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue'),
  },
]
